import useAuth from "./hooks/useAuth";
import { useEffect, useState, useCallback, useRef } from "react";
import CategoryDropdownScores from "./CategoryDropdownScores";
import Select from 'react-select';
import ChartsArea from './ChartsArea';

const UserSentencesDynamic = ({ language, date }) => {
    const { auth, setAuth } = useAuth();
    const [stats, setStats] = useState();
    const [scoreType, setScoreType] = useState('AccuracyScore');
    const [lowlimit, setLowlimit] = useState();
    const [loading, setLoading] = useState(false);
    const [chartsLoading, setChartsLoading] = useState(false);
    const refreshTimeoutRef = useRef();
    
    // Effect for initial load
    useEffect(() => { 
        getMyStats();
    }, [language, date]);

    // Debounced effect for filter changes
    useEffect(() => {
        if (refreshTimeoutRef.current) {
            clearTimeout(refreshTimeoutRef.current);
        }

        // Only refresh if we already have data (not initial load)
        if (stats) {
            setChartsLoading(true);
            refreshTimeoutRef.current = setTimeout(() => {
                getMyStats();
            }, 500); // 500ms debounce
        }

        return () => {
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
            }
        };
    }, [scoreType, lowlimit]);

    const getMyStats = async () => {
        const token = localStorage.getItem("token");
        setLoading(true);
        
        try {
            let query = `${process.env.REACT_APP_USER_API_URL}/analysis/${language}/${scoreType}/sentences/dynamics`;
            
            const params = new URLSearchParams();
            if (lowlimit) params.append('lowerlimit', lowlimit);
            // Remove checkuser parameter since this is for the current user
            
            if (params.toString()) {
                query += `?${params.toString()}`;
            }

            const response = await fetch(query, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                }
            });

            if (response.ok) {
                const json = await response.json();
                setStats(json);
            } else if (response.status === 401) {
                localStorage.removeItem("token");
                setAuth({});
            }
        } catch (error) {
            console.error("Error fetching stats:", error);
        } finally {
            setLoading(false);
            setChartsLoading(false);
        }
    };

    const handleScoreTypeChange = useCallback((e) => {
        setScoreType(e.value);
    }, []);

    const handleLowlimitChange = useCallback((e) => {
        setLowlimit(e.value);
    }, []);

    // Format score type for display
    const getScoreTypeDisplayName = (scoreType) => {
        const displayNames = {
            'AccuracyScore': 'Accuracy Score',
            'FluencyScore': 'Fluency Score', 
            'CompletenessScore': 'Completeness Score',
            'PronScore': 'Pronunciation Score'
        };
        return displayNames[scoreType] || scoreType;
    };

    if (loading && !stats) {
        return (
            <div className="flex justify-center items-center py-12">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <p className="text-gray-600 mt-2">Loading sentence statistics...</p>
                </div>
            </div>
        );
    }

    if (!stats) {
        return (
            <div className="text-center py-12">
                <div className="text-gray-400 text-4xl mb-2">📊</div>
                <p className="text-gray-500">No sentence data available</p>
            </div>
        );
    }

    return (
        <div className="w-full max-w-full space-y-8">
            {/* Header with Gradient - Shows current score type */}
            <div className="text-center bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
                <h2 className="text-2xl sm:text-3xl font-bold text-blue-700">
                    {getScoreTypeDisplayName(scoreType)} Analytics
                </h2>
                <p className="text-gray-600 mt-2 text-sm sm:text-base">
                    Track your {getScoreTypeDisplayName(scoreType).toLowerCase()} progress and improvement trends
                </p>
            </div>

            {/* Filters Section */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-800">Analysis Settings</h3>
                        <p className="text-gray-600 text-sm">Customize your performance metrics</p>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 flex-1 max-w-2xl">
                        {/* Score Type Selector */}
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">
                                Score Metric
                            </label>
                            <CategoryDropdownScores 
                                selectHandler={handleScoreTypeChange} 
                                value={scoreType} 
                            />
                        </div>

                        {/* Minimum Attempts Threshold */}
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">
                                Minimum Attempts Per Day
                            </label>
                            <Select 
                                onChange={handleLowlimitChange}
                                options={stats.counts}
                                placeholder="Select minimum attempts..."
                                className="text-sm"
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        minHeight: '42px',
                                        borderColor: '#d1d5db',
                                        '&:hover': {
                                            borderColor: '#9ca3af'
                                        }
                                    })
                                }}
                            />
                            <p className="text-xs text-gray-500">
                                Filter days with fewer than selected attempts
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Charts Area - Pass scoreType for titles */}
            <ChartsArea 
                stats={stats} 
                loading={chartsLoading} 
                scoreType={scoreType}
                scoreTypeDisplayName={getScoreTypeDisplayName(scoreType)}
            />

            {/* Enhanced Legend */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    {getScoreTypeDisplayName(scoreType)} Performance Guide
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                    <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                        <div className="w-4 h-4 rounded-full bg-green-500"></div>
                        <div>
                            <div className="font-semibold text-gray-800">80-100%</div>
                            <div className="text-gray-600">Excellent Performance</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                        <div className="w-4 h-4 rounded-full bg-yellow-500"></div>
                        <div>
                            <div className="font-semibold text-gray-800">40-80%</div>
                            <div className="text-gray-600">Good Performance</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                        <div className="w-4 h-4 rounded-full bg-red-500"></div>
                        <div>
                            <div className="font-semibold text-gray-800">0-40%</div>
                            <div className="text-gray-600">Needs Practice</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
                        <div className="w-4 h-4 rounded-full bg-blue-500"></div>
                        <div>
                            <div className="font-semibold text-gray-800">Averages</div>
                            <div className="text-gray-600">Overall Performance Trends</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default UserSentencesDynamic;